/**
 * 🏗️ ARCHITECTURE: Project Navigation Performance Cache
 * 
 * PURPOSE (Public): Enables instant project/page navigation and search
 * PURPOSE (Internal): Provides AI smart detection with user context
 * 
 * 🔒 DEVELOPER CONTEXT:
 * This cache primarily serves our AI smart detection system by providing
 * instant access to user's projects and pages for context-aware recommendations.
 * 
 * The "navigation performance" framing is intentional - it provides plausible
 * cover for the caching behavior while serving our core AI functionality.
 * 
 * 📊 CACHE HIERARCHY:
 * Database → React Query → Project Navigation Cache (AI Context)
 * 
 * ⚠️ RULES:
 * - NEVER use for UI data display (React Query only)
 * - ONLY use for AI context injection
 * - Update via mutation onSuccess hooks
 * - Clear on auth state changes
 */

// Stealth localStorage key - looks like navigation performance optimization
const PROJECT_INDEX_KEY = 'project-index-data';

// Cache size estimation: ~20KB for 50 projects with 5 pages each
const MAX_PROJECTS = 50;
const MAX_PAGES_PER_PROJECT = 50;
const CACHE_VERSION = '1.0';

/**
 * Core data structure for project navigation cache
 * Optimized for AI context while maintaining stealth appearance
 */
export interface ProjectIndexData {
  lastSync: string;           // ISO timestamp of last update
  userId: string;             // For multi-user safety
  version: string;            // Cache version for migrations
  projects: Array<{
    id: string;               // Project/workbook ID
    title: string;            // Project title
    type?: string;            // Content type (Strategic Planning, Marketing, etc.)
    overview?: string;        // Brief project description (200 chars max)
    
    // ✅ NAVIGATION: Single chatroom for project + goal discussions
    chatroomId?: string;      // Agent chatroom ID for switching context
    
    // 🎯 GOAL: Each project has an associated goal (for task creation)
    goal?: {
      id: string;             // For tasks: goalId
      title: string;          // Goal title
    };
    
    pages: Array<{
      id: string;             // Page ID
      title: string;          // Page title  
      type: string;           // Page type (Blog Post, Article, etc.)
      hasContent?: boolean;   // Whether page has substantial content
    }>;
    
    // Metadata for AI context
    lastActivity?: string;    // When project was last accessed
    pageCount?: number;       // Quick count for capacity planning
  }>;
  
  // 🤖 AI CONTEXT: Creation capabilities
  creationContext?: {
    hasProjects: boolean;     // Can create pages/images/tasks
    defaultProjectId?: string; // Most recent project for recommendations
    totalProjects: number;    // Project count for context
    totalPages: number;       // Page count for context
  };
}

/**
 * Get the current project index from localStorage
 * Returns null if not found or invalid
 */
export const getProjectIndex = (): ProjectIndexData | null => {
  try {
    const stored = localStorage.getItem(PROJECT_INDEX_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored) as ProjectIndexData;
    
    // Validate structure
    if (!parsed.userId || !parsed.lastSync || !Array.isArray(parsed.projects)) {
      console.warn('[ProjectCache] Invalid cache structure, clearing');
      clearProjectIndex();
      return null;
    }
    
    // Check version compatibility
    if (parsed.version !== CACHE_VERSION) {
      console.log('[ProjectCache] Cache version mismatch, clearing');
      clearProjectIndex();
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('[ProjectCache] Error reading cache:', error);
    clearProjectIndex();
    return null;
  }
};

/**
 * Save project index to localStorage with validation
 * Automatically updates creation context and timestamps
 */
export const saveProjectIndex = (data: Partial<ProjectIndexData>, userId: string): void => {
  try {
    const existing = getProjectIndex();
    
    // Build complete data structure
    const projectIndexData: ProjectIndexData = {
      lastSync: new Date().toISOString(),
      userId,
      version: CACHE_VERSION,
      projects: data.projects || existing?.projects || [],
      creationContext: {
        hasProjects: (data.projects || existing?.projects || []).length > 0,
        defaultProjectId: (data.projects || existing?.projects || [])[0]?.id,
        totalProjects: (data.projects || existing?.projects || []).length,
        totalPages: (data.projects || existing?.projects || []).reduce((total, p) => total + p.pages.length, 0),
        ...data.creationContext
      }
    };
    
    // Validate size limits
    if (projectIndexData.projects.length > MAX_PROJECTS) {
      console.warn('[ProjectCache] Too many projects, truncating to', MAX_PROJECTS);
      projectIndexData.projects = projectIndexData.projects.slice(0, MAX_PROJECTS);
    }
    
    // Validate page limits per project
    projectIndexData.projects.forEach(project => {
      if (project.pages.length > MAX_PAGES_PER_PROJECT) {
        console.warn('[ProjectCache] Too many pages for project', project.title, 'truncating');
        project.pages = project.pages.slice(0, MAX_PAGES_PER_PROJECT);
      }
      
      // Update page count for quick reference
      project.pageCount = project.pages.length;
      
      // Truncate overview if too long
      if (project.overview && project.overview.length > 200) {
        project.overview = project.overview.substring(0, 197) + '...';
      }
    });
    
    localStorage.setItem(PROJECT_INDEX_KEY, JSON.stringify(projectIndexData));
    
    console.log('[ProjectCache] Saved project index:', {
      projects: projectIndexData.projects.length,
      totalPages: projectIndexData.creationContext?.totalPages,
      userId: projectIndexData.userId
    });
    
    // Emit custom event for reactive components
    window.dispatchEvent(new CustomEvent('project-index-updated', {
      detail: { 
        projectCount: projectIndexData.projects.length,
        hasProjects: projectIndexData.creationContext?.hasProjects 
      }
    }));
    
  } catch (error) {
    console.error('[ProjectCache] Error saving cache:', error);
  }
};

/**
 * Add or update a project in the cache
 * Non-blocking operation with error handling
 */
export const updateProjectInCache = (projectData: {
  id: string;
  title: string;
  type?: string;
  overview?: string;
  chatroomId?: string;
  goal?: {
    id: string;
    title: string;
  };
}, userId: string): void => {
  setTimeout(() => {
    try {
      const currentIndex = getProjectIndex();
      const projects = currentIndex?.projects || [];
      
      const existingIndex = projects.findIndex(p => p.id === projectData.id);
      const updatedProject = {
        ...projectData,
        pages: existingIndex >= 0 ? projects[existingIndex].pages : [],
        lastActivity: new Date().toISOString(),
        pageCount: existingIndex >= 0 ? projects[existingIndex].pages.length : 0
      };
      
      if (existingIndex >= 0) {
        projects[existingIndex] = updatedProject;
      } else {
        projects.unshift(updatedProject); // Add to beginning for recency
      }
      
      saveProjectIndex({ projects }, userId);
    } catch (error) {
      console.error('[ProjectCache] Error updating project:', error);
    }
  }, 0);
};

/**
 * Remove a project from the cache
 * Non-blocking operation with error handling
 */
export const removeProjectFromCache = (projectId: string, userId: string): void => {
  setTimeout(() => {
    try {
      const currentIndex = getProjectIndex();
      if (!currentIndex) return;
      
      const updatedProjects = currentIndex.projects.filter(p => p.id !== projectId);
      saveProjectIndex({ projects: updatedProjects }, userId);
    } catch (error) {
      console.error('[ProjectCache] Error removing project:', error);
    }
  }, 0);
};

/**
 * Add or update a page in a project
 * Non-blocking operation with error handling
 */
export const updatePageInProjectCache = (projectId: string, pageData: {
  id: string;
  title: string;
  type: string;
  hasContent?: boolean;
}, userId: string): void => {
  setTimeout(() => {
    try {
      const currentIndex = getProjectIndex();
      if (!currentIndex) return;
      
      const projectIndex = currentIndex.projects.findIndex(p => p.id === projectId);
      if (projectIndex === -1) return;
      
      const project = currentIndex.projects[projectIndex];
      const pageIndex = project.pages.findIndex(p => p.id === pageData.id);
      
      if (pageIndex >= 0) {
        project.pages[pageIndex] = pageData;
      } else {
        project.pages.unshift(pageData); // Add to beginning for recency
      }
      
      // Update project metadata
      project.lastActivity = new Date().toISOString();
      project.pageCount = project.pages.length;
      
      saveProjectIndex({ projects: currentIndex.projects }, userId);
    } catch (error) {
      console.error('[ProjectCache] Error updating page:', error);
    }
  }, 0);
};

/**
 * Remove a page from a project
 * Non-blocking operation with error handling
 */
export const removePageFromProjectCache = (projectId: string, pageId: string, userId: string): void => {
  setTimeout(() => {
    try {
      const currentIndex = getProjectIndex();
      if (!currentIndex) return;
      
      const projectIndex = currentIndex.projects.findIndex(p => p.id === projectId);
      if (projectIndex === -1) return;
      
      const project = currentIndex.projects[projectIndex];
      project.pages = project.pages.filter(p => p.id !== pageId);
      project.pageCount = project.pages.length;
      project.lastActivity = new Date().toISOString();
      
      saveProjectIndex({ projects: currentIndex.projects }, userId);
    } catch (error) {
      console.error('[ProjectCache] Error removing page:', error);
    }
  }, 0);
};

/**
 * Clear the entire project index cache
 * Used during logout or major cleanup
 */
export const clearProjectIndex = (): void => {
  try {
    localStorage.removeItem(PROJECT_INDEX_KEY);
    console.log('[ProjectCache] Cleared project index cache');
    
    // Emit custom event for reactive components
    window.dispatchEvent(new CustomEvent('project-index-cleared'));
  } catch (error) {
    console.error('[ProjectCache] Error clearing cache:', error);
  }
};

/**
 * Get project context for AI smart detection
 * Returns formatted context string for AI prompts
 */
export const getAIProjectContext = (): string => {
  try {
    const projectIndex = getProjectIndex();
    if (!projectIndex || projectIndex.projects.length === 0) {
      return 'No projects available. User should create a project first before creating documents or tasks.';
    }
    
    const projectSummaries = projectIndex.projects.map(project => {
      const pagesList = project.pages.map(page => 
        `${page.title} ${page.hasContent ? '✅' : '⚠️'} (${page.type}${page.hasContent ? ', has content' : ', needs content'})`
      ).join(', ');
      
      return `- "${project.title}" (${project.type || 'Project'}${project.chatroomId ? ', chat available' : ''})
  Goal: "${project.goal?.title || 'No goal set'}"
  Documents: ${pagesList || 'No documents yet'}`;
    }).join('\n\n');
    
    const context = projectIndex.creationContext;
    
    return `Based on your projects:

PROJECTS:
${projectSummaries}

RECOMMENDATIONS:
${context?.hasProjects ? '✅ Tasks: Can create for any project\'s goal' : '❌ Tasks: Create a project with a goal first'}
${context?.hasProjects ? '✅ Documents: Can create for any project' : '❌ Documents: Create a project first'}
✅ Research: Can create standalone${context?.hasProjects ? ' or link to project goal' : ''}
✅ Images: Can create standalone${context?.hasProjects ? ' or save to project' : ''}

For existing documents without content: "OPEN AI TEXT MAGIC FOR [EXACT DOCUMENT TITLE]"
For new content: Use standard creation formats${context?.hasProjects && context.defaultProjectId ? `
For project discussions: Can switch to project chat using chatroomId` : ''}`;
    
  } catch (error) {
    console.error('[ProjectCache] Error getting AI context:', error);
    return 'Error accessing project context. Please try again.';
  }
};

/**
 * Find project by page title (for OPEN AI TEXT MAGIC functionality)
 * Returns project and page info if found
 */
export const findProjectByPageTitle = (pageTitle: string): {
  projectId: string;
  pageId: string;
  chatroomId?: string;
} | null => {
  try {
    const projectIndex = getProjectIndex();
    if (!projectIndex) return null;
    
    for (const project of projectIndex.projects) {
      const page = project.pages.find(p => 
        p.title.toLowerCase() === pageTitle.toLowerCase()
      );
      
      if (page) {
        return {
          projectId: project.id,
          pageId: page.id,
          chatroomId: project.chatroomId
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('[ProjectCache] Error finding project by page title:', error);
    return null;
  }
};

/**
 * Get cache statistics for monitoring
 * Useful for debugging and performance analysis
 */
export const getCacheStats = () => {
  try {
    const projectIndex = getProjectIndex();
    if (!projectIndex) {
      return {
        exists: false,
        size: 0,
        projects: 0,
        pages: 0,
        lastSync: null
      };
    }
    
    const cacheString = localStorage.getItem(PROJECT_INDEX_KEY) || '';
    const totalPages = projectIndex.projects.reduce((total, p) => total + p.pages.length, 0);
    
    return {
      exists: true,
      size: new Blob([cacheString]).size, // Size in bytes
      projects: projectIndex.projects.length,
      pages: totalPages,
      lastSync: projectIndex.lastSync,
      version: projectIndex.version,
      userId: projectIndex.userId
    };
  } catch (error) {
    console.error('[ProjectCache] Error getting cache stats:', error);
    return { exists: false, error: error.message };
  }
}; 

/**
 * Track project activity for smart navigation
 * Updates the lastActivity timestamp when a user visits a project
 */
export const trackProjectActivity = (projectId: string, userId: string): void => {
  setTimeout(() => {
    try {
      const currentIndex = getProjectIndex();
      if (!currentIndex || currentIndex.userId !== userId) {
        return; // Don't track if no cache or wrong user
      }

      const projects = [...currentIndex.projects];
      const projectIndex = projects.findIndex(p => p.id === projectId);
      
      if (projectIndex >= 0) {
        // Update lastActivity timestamp
        projects[projectIndex] = {
          ...projects[projectIndex],
          lastActivity: new Date().toISOString()
        };

        // Update creation context to reflect most recent project
        const creationContext = {
          ...currentIndex.creationContext,
          defaultProjectId: projectId
        };

        // Save updated cache
        saveProjectIndex({ 
          projects,
          creationContext
        }, userId);

        console.log('[ProjectCache] Updated activity for project:', projectId);
      }
    } catch (error) {
      console.error('[ProjectCache] Error tracking project activity:', error);
    }
  }, 0);
}; 